import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { RadioInput } from '@hivebrite/design-system/Inputs';
import { useBreakpoint } from '@hivebrite/design-system/Theme';

import { FreeTrailPeriodBadge } from 'hb-react/shared/apps/Memberships/components/FreeTrialPeriodBadge';
import { translations } from 'hb-react/shared/apps/Memberships/utils/translations';
import { Price as DesignPrice } from 'hb-react/shared/components/Price';

import FixedEndWarning from '../FixedEndWarning';

const PurchaseOption = ({
  id,
  name,
  description,
  price,
  currency,
  periodicity,
  validity,
  selected = false,
  disabled = false,
  enable_expire_month: enableExpireMonth = false,
  expire_month: expireMonth,
  onSelect,
  cartItem = {},
  ...props
}) => {
  const optionSelected = Boolean(cartItem?.id || selected);

  return (
    <>
      <Content
        currency={currency}
        description={description}
        id={id}
        name={name}
        onSelect={onSelect}
        periodicity={periodicity}
        price={price}
        selected={optionSelected}
        disabled={disabled}
        {...props}
      />
      {optionSelected && enableExpireMonth && (
        <FixedEndWarning validity={validity} expireMonth={expireMonth} />
      )}
    </>
  );
};

const Content = ({
  currency,
  description,
  id,
  name,
  onSelect,
  periodicity,
  price,
  selected,
  has_free_trial: hasFreeTrial = false,
  free_trial_period_periodicity: freeTrialPeriodPeriodicity,
  disabled = false,
}) => {
  const breakpoint = useBreakpoint();

  const verticalLayout = ['s', 'm'].includes(breakpoint);

  return (
    <StyledWrapper>
      <RadioInput
        key={verticalLayout ? `vertical-${id}` : `horizontal-${id}`}
        id={`purchase-option-${id}`}
        name={`purchase_option-${id}`}
        aria-label={name}
        disabled={disabled}
        value={`${id}`}
        checked={selected}
        onChange={onSelect}
        labelMultiline
        alignItems="flex-start"
        inputProps={{
          style: {
            top: 1,
          },
        }}
        labelStyle={{
          flexWrap: verticalLayout ? 'wrap' : 'nowrap',
        }}
        label={
          <Box width="100%">
            <Box flexDirection="column" flex="1 1 100%">
              <Name name={name} />
              <Description description={description} />
            </Box>
            <Box
              flexDirection="column"
              flex="0 0 auto"
              mt={verticalLayout ? 2 : 0}
              ml={verticalLayout ? 0 : 8}
              alignItems="flex-end"
            >
              <Box flexDirection="row" alignItems="flex-start">
                <Box>{hasFreeTrial && <FreeTrailPeriodBadge mt={0} />}</Box>
                <Box flexDirection="column" alignItems="flex-start">
                  <Box font="h4-semibold">
                    {hasFreeTrial && (
                      <TextEllipsis>
                        {`${s__('Memberships|Free')} / ${n__(
                          '%{days} day',
                          '%{days} days',
                          freeTrialPeriodPeriodicity,
                        ).i__({
                          days: freeTrialPeriodPeriodicity,
                        })}`}
                      </TextEllipsis>
                    )}
                  </Box>
                  <Box font="h4-semibold">
                    <Price
                      price={price}
                      currency={currency}
                      periodicity={periodicity}
                      hasFreeTrial={hasFreeTrial}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        }
      />
    </StyledWrapper>
  );
};

const Name = ({ name }) => <Box font="h4-semibold">{name}</Box>;

const Description = ({ description }) => (
  <Box font="body-dove-italic" mt={description ? 1 : 0}>
    {description}
  </Box>
);

const Price = ({ price, currency, periodicity, hasFreeTrial }) => (
  <Box font="h4-semibold">
    <DesignPrice
      price={price}
      currency={currency}
      hasFreeTrial={hasFreeTrial}
    />
    <>&nbsp;{`/ ${translations.periodicity[periodicity]}`}</>
  </Box>
);

export default PurchaseOption;

const TextEllipsis = styled.span`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const StyledWrapper = styled(Box)`
  label {
    width: 100%;
  }
`;
